import type {
    TApiDealFileType,
    TApiFileStatus,
    TApiFileType,
    TApiGlType,
    TApiSubmissionContext,
} from "@/apis/proda_finance/types";

export type TSubmissionSlot = {
    /**
     * The backend `FileType` enum value ("trial_balance", "general_ledger", …) —
     * NOT the store slice's "tb"/"gl" shorthand. Typed as the union so a
     * mismatch is a compile error rather than a blank label in the header.
     */
    fileType: TApiFileType;
    expected: boolean;
    fileId: string | null;
    originalFilename: string | null;
    /**
     * Typed as the API union rather than a bare string so `StatusBadge` accepts it
     * without a cast — `TFileStatus` and `TApiFileStatus` are the same literal union.
     */
    status: TApiFileStatus | null;
    uploadedAt: string | null;
    uploadedByName: string | null;
    /** Byte size of the stored original, or null for rows uploaded before the column existed. */
    fileSizeBytes: number | null;
    /** The file's own period (`YYYY-MM-DD`); null when the slot is empty. */
    period: string | null;
    createdAt: string | null;
};

export type TSubmissionEtlArtifact = {
    kind: "finjournal" | "data_platform";
    available: boolean;
    sourceFileId: string;
    persisted: boolean;
    fileName: string | null;
    generatedAt: string | null;
    generatedByName: string | null;
};

export type TSubmissionContext = {
    fileId: string;
    period: string;
    /** Today on the business clock (Asia/Shanghai), server-computed. */
    today: string;
    businessPartnerName: string | null;
    submissionName: string | null;
    dealId: string | null;
    dealFileType: TApiDealFileType | null;
    glType: TApiGlType | null;
    months: string[];
    slots: TSubmissionSlot[];
    etlArtifacts: TSubmissionEtlArtifact[];
    /**
     * The caller may not review this file and reached the page through another
     * grant (today: `supporting_files_view`). The shell renders header + Supporting
     * Files only, and `slots` is empty.
     *
     * This is the *authority* on restricted mode, not a hint. The shell cannot
     * decide it from permissions alone: whether a caller may review depends on the
     * file's type, and a restricted caller never loads the file to find out. See
     * Task 7.
     */
    restricted: boolean;
};

/**
 * Map the submission-context payload to the shape the v2 shell renders.
 *
 * A standalone FinJournal has no deal, so every deal-derived field goes null and
 * the header falls back to the file's own name with an empty month picker.
 */
export function mapApiSubmissionContext(api: TApiSubmissionContext): TSubmissionContext {
    return {
        fileId: api.file_id,
        period: api.period,
        today: api.today,
        businessPartnerName: api.deal?.service_provider_name ?? null,
        submissionName: api.deal?.name ?? null,
        dealId: api.deal?.id ?? null,
        dealFileType: api.deal?.deal_file_type ?? null,
        glType: api.deal?.gl_type ?? null,
        months: api.months,
        slots: api.slots.map((slot) => ({
            fileType: slot.file_type,
            expected: slot.expected,
            fileId: slot.file?.id ?? null,
            originalFilename: slot.file?.original_filename ?? null,
            status: slot.file?.status ?? null,
            uploadedAt: slot.file?.uploaded_at ?? null,
            uploadedByName: slot.file?.uploaded_by_name ?? null,
            fileSizeBytes: slot.file?.file_size_bytes ?? null,
            period: slot.file?.period ?? null,
            createdAt: slot.file?.created_at ?? null,
        })),
        etlArtifacts: api.etl_artifacts.map((artifact) => ({
            kind: artifact.kind,
            available: artifact.available,
            sourceFileId: artifact.source_file_id,
            persisted: artifact.persisted,
            fileName: artifact.file_name,
            generatedAt: artifact.generated_at,
            generatedByName: artifact.generated_by_name,
        })),
        restricted: api.restricted,
    };
}

/** `YYYY-MM-DD` → `YYYY-MM`: the API speaks dates, the picker and the URL speak months. */
export function toYearMonth(period: string): string {
    return period.slice(0, 7);
}

/**
 * The active file of `fileType` in the context's period, or null when that month
 * holds none.
 *
 * A null answer is what the empty state is built on, so the caller must first
 * check that the context actually describes the month it is asking about — while a
 * month switch is in flight the context still holds the previous month's slots.
 */
export function resolveSlotFileId(
    context: TSubmissionContext | null,
    fileType: string,
): string | null {
    if (context === null) return null;
    const slot = context.slots.find((candidate) => candidate.fileType === fileType);
    return slot?.fileId ?? null;
}

/** The ends of the picker's range, or null when the submission offers no months. */
export function monthBounds(
    context: TSubmissionContext | null,
): { min: string; max: string } | null {
    if (context === null || context.months.length === 0) return null;
    // Lexicographic order is chronological order for `YYYY-MM`, so this needs no
    // date parsing — and it does not lean on the API's ordering.
    const months = context.months.map(toYearMonth).sort();
    return { min: months[0]!, max: months[months.length - 1]! };
}

// UTC because the value is a bare month with no instant in it — a local-zone
// reading of "2026-03-01" can land in February.
const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
});

/** `"2026-03"` → `"March 2026"`, for prose rather than a control. */
export function formatMonthLabel(yearMonth: string): string {
    return MONTH_LABEL_FORMATTER.format(new Date(`${toYearMonth(yearMonth)}-01T00:00:00Z`));
}

/** The `?type=` vocabulary: short, URL-friendly names for the reviewable file types. */
export type TReviewTypeSlug = "tb" | "gl" | "otb" | "fj";

/**
 * The one literal record `toTypeSlug`/`fromTypeSlug` are both built from, so the
 * two directions cannot drift apart. `tb_gl` deliberately has no entry — combined
 * TB_GL uploads are split into separate TB and GL rows at upload time, so a
 * `tb_gl` file never reaches the review page and there is nothing for a slug to
 * select.
 */
const TYPE_SLUG_TO_FILE_TYPE: Record<TReviewTypeSlug, TApiFileType> = {
    tb: "trial_balance",
    gl: "general_ledger",
    otb: "open_trial_balance",
    fj: "financial_journals",
};

/** Backend wire value → `?type=` slug, or null for anything with no slug (notably `tb_gl`). */
export function toTypeSlug(fileType: string): TReviewTypeSlug | null {
    const entry = Object.entries(TYPE_SLUG_TO_FILE_TYPE).find(
        ([, wireType]) => wireType === fileType,
    );
    return entry !== undefined ? (entry[0] as TReviewTypeSlug) : null;
}

/** `?type=` slug → backend wire value, or null for a hand-typed value that names no type. */
export function fromTypeSlug(raw: string): TApiFileType | null {
    return raw in TYPE_SLUG_TO_FILE_TYPE ? TYPE_SLUG_TO_FILE_TYPE[raw as TReviewTypeSlug] : null;
}

/**
 * The slot types this submission's context actually offers for its period — the
 * same "expected, or already holds a file" filter `FileReviewTabs` applies inline,
 * shared here so this resolver and that component cannot disagree.
 */
export function offeredSlotTypes(context: TSubmissionContext | null): TApiFileType[] {
    if (context === null) return [];
    return context.slots
        .filter((slot) => slot.expected || slot.fileId !== null)
        .map((slot) => slot.fileType);
}

/**
 * The file type the page is reviewing: the URL's `?type=` when it names a type
 * this submission offers, otherwise the open file's own type.
 *
 * The context argument is the one already narrowed to the selected period by the
 * caller — a slot set read from a different month would answer about the wrong
 * submission. A null context (nothing loaded yet) trusts the slug rather than
 * falling back, because falling back would flash the open file's type into the
 * selector for the length of the first load and then correct itself.
 */
export function resolveSelectedFileType(
    slugParam: string | null,
    context: TSubmissionContext | null,
    openFileType: string,
): string {
    if (slugParam === null) return openFileType;
    const requestedType = fromTypeSlug(slugParam);
    if (requestedType === null) return openFileType;
    if (context === null) return requestedType;
    return offeredSlotTypes(context).includes(requestedType) ? requestedType : openFileType;
}
